import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  ChevronDown,
  FolderKanban,
  GripVertical,
  ListTodo,
  MoreHorizontal,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  MobileListCard,
  MobileListCardStack,
} from '@/components/ui/MobileListCard';
import { useIsMobile } from '@/lib/useMediaQuery';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { HEXAGON_PATH } from '@/lib/brand';
import { formatRelative } from '@/lib/formatters';
import {
  taskKeys,
  useArchiveProject,
  useDeleteProject,
  useMyOpenTasks,
  useMyWorkloadThisWeek,
  useProjectsWithSummary,
  useReorderProjects,
  useUpdateTask,
  type MyTaskRow,
  type ProjectSummary,
} from '@/features/projects/hooks';
import {
  formatWorkloadHours,
  isHeavyDay,
  isHeavyWeek,
  WORKDAY_HOURS,
  WORKWEEK_HOURS,
} from '@/features/projects/workload';
import {
  PRIORITY_META,
  STATUS_META,
  completionPercent,
  dueState,
  type DueState,
} from '@/features/projects/display';
import {
  DEPARTMENTS,
  departmentMeta,
  parseDepartmentParam,
  type DepartmentSlug,
} from '@/features/projects/departments';
import { useAuth } from '@/app/AuthProvider';
import { NewProjectDialog } from '@/components/projects/NewProjectDialog';
import {
  AvatarStack,
  CompletionBar,
  DueChip,
  ProjectMonogram,
} from '@/components/projects/ProjectVisuals';
import {
  ActionSheet,
  ActionSheetItem,
} from '@/components/projects/MobileSheets';

/**
 * My tasks — the landing page of the Tasks section.
 *
 * The Tasks section is the hub that links every department's projects
 * together: whatever department owns a project (Engineering today,
 * Marketing Projects later, …), the work assigned to you lands here.
 * The page opens with your own queue — overdue, due today, upcoming,
 * then the undated backlog — followed by a Humus pulse across the
 * active projects and the project grid itself. A `?department=` filter
 * scopes everything on the page to one department, which is also the
 * link a department section can point at for "its" projects.
 *
 * Closed (archived) projects live in a collapsible section below the
 * active list so they can still be referenced without crowding the
 * day-to-day view.
 */
// react-query invalidation can briefly surface a stale ordering after
// a drop, so we keep two id-list droppables (active + archived) and
// dispatch reorder mutations against a single shared `useReorderProjects`
// hook. The server collapses the writes onto one global `position`
// sequence per organization — splitting active/archived in the UI is
// purely about not letting the user drop a card into a different
// archive bucket.
const DROPPABLE_ACTIVE = 'projects:active';
const DROPPABLE_ARCHIVED = 'projects:archived';

const PROJECT_GRID = 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3';

export function MyTasksPage() {
  const [newOpen, setNewOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Drag-to-reorder relies on a fine pointer; on phones the grid is a
  // plain list so a vertical swipe always scrolls the page.
  const isMobile = useIsMobile();

  // The department filter lives in the URL so it survives reloads and
  // so a department section can deep-link straight into its slice.
  const [searchParams, setSearchParams] = useSearchParams();
  const department = parseDepartmentParam(searchParams.get('department'));
  const setDepartment = (next: DepartmentSlug | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('department', next);
    else params.delete('department');
    setSearchParams(params, { replace: true });
  };

  // We always pull including-archived so the toggle can flip without
  // refetching. The server-side hide-archived path on `useProjects`
  // is still useful for the sidebar (which never wants the archived
  // entries), but here we do the split client-side — and the
  // department filter too, so switching chips is instant.
  const { data: projects, isLoading } = useProjectsWithSummary({
    includeArchived: true,
  });
  const reorder = useReorderProjects();

  const { active, archived, departmentsInUse } = useMemo(() => {
    const a: ProjectSummary[] = [];
    const z: ProjectSummary[] = [];
    const used = new Set<string>();
    for (const p of projects ?? []) {
      if (p.department) used.add(p.department);
      if (department && p.department !== department) continue;
      if (p.archived_at) z.push(p);
      else a.push(p);
    }
    return { active: a, archived: z, departmentsInUse: used };
  }, [projects, department]);

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;
    // We deliberately disallow cross-bucket drops. Moving a card from
    // the archived grid into the active grid (or vice versa) is an
    // archive/un-archive action — operators do that through the
    // dropdown menu so the confirmation + toast pipeline stays the
    // single source of truth for that mutation.
    if (destination.droppableId !== source.droppableId) return;
    if (destination.index === source.index) return;

    const list =
      source.droppableId === DROPPABLE_ACTIVE ? active : archived;
    const next = list.slice();
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);

    // Merge back into the full ordering: keep the un-touched bucket
    // in its current relative order, slot the freshly reordered
    // bucket back in. The server stores ONE global sequence so we
    // ship the union — keeps active + archived from drifting apart.
    const merged =
      source.droppableId === DROPPABLE_ACTIVE
        ? [...next, ...archived]
        : [...active, ...next];

    reorder.mutate(
      { orderedIds: merged.map((p) => p.id) },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Reorder failed';
          toast.error(msg);
        },
      },
    );
  };

  const newProjectButton = (
    <Button onClick={() => setNewOpen(true)} className="w-full sm:w-auto">
      <Plus className="h-4 w-4" />
      New project
    </Button>
  );

  const departmentLabel = department ? departmentMeta(department)?.label : null;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Tasks"
        title="My tasks"
        actions={newProjectButton}
      />

      <DepartmentFilter
        value={department}
        onChange={setDepartment}
        inUse={departmentsInUse}
      />

      <MyTasksCard department={department} />

      {isLoading ? (
        <Skeleton className="h-48 w-full rounded-lg" />
      ) : (
        active.length > 0 && <TasksPulse active={active} department={department} />
      )}

      {isLoading ? (
        <div className={PROJECT_GRID}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      ) : active.length === 0 && archived.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title={
            departmentLabel
              ? `No ${departmentLabel} projects yet`
              : 'No projects yet'
          }
          action={newProjectButton}
        />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <section className="space-y-3">
            <SectionLabel count={active.length}>
              {departmentLabel
                ? `Active ${departmentLabel} projects`
                : 'Active projects'}
            </SectionLabel>
            {active.length === 0 ? (
              <EmptyState
                icon={FolderKanban}
                title="No active projects"
                action={newProjectButton}
              />
            ) : (
              <ProjectGrid
                projects={active}
                droppableId={DROPPABLE_ACTIVE}
                // Positions are one global sequence, so reordering a
                // department slice would scramble the projects that
                // aren't on screen. Clear the filter to reorder.
                draggable={!isMobile && !department}
              />
            )}
          </section>

          {archived.length > 0 && (
            <section className="space-y-3 pt-2">
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="-mx-2 flex min-h-11 items-center gap-2 rounded-lg px-2 text-xs uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                aria-expanded={showArchived}
              >
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    !showArchived && '-rotate-90',
                  )}
                  strokeWidth={2}
                />
                <span>Archived</span>
                <Badge variant="muted" className="rounded-full">
                  {archived.length}
                </Badge>
              </button>
              {showArchived && (
                <ProjectGrid
                  projects={archived}
                  droppableId={DROPPABLE_ARCHIVED}
                  draggable={!isMobile && !department}
                />
              )}
            </section>
          )}
        </DragDropContext>
      )}

      <NewProjectDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        defaultDepartment={department}
      />
    </div>
  );
}

function SectionLabel({
  children,
  count,
}: {
  children: React.ReactNode;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {children}
      </h2>
      <Badge variant="info-soft" className="px-2 py-0 tabular-nums">
        {count}
      </Badge>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Department filter — one chip per department that owns at least one
// project, plus "All". Departments with no projects yet stay hidden so
// the row doesn't fill with empty filters; a department section can
// still deep-link to its slug and the chip appears once it has a
// project.
// ---------------------------------------------------------------------------

function DepartmentFilter({
  value,
  onChange,
  inUse,
}: {
  value: DepartmentSlug | null;
  onChange: (next: DepartmentSlug | null) => void;
  inUse: Set<string>;
}) {
  const options = DEPARTMENTS.filter(
    (d) => inUse.has(d.slug) || d.slug === value,
  );
  if (options.length === 0) return null;

  const chipClass = (selected: boolean) =>
    cn(
      'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
      selected
        ? 'border-humus bg-humus text-linen'
        : 'border-border bg-card text-muted-foreground hover:border-old-growth/40 hover:text-foreground',
    );

  return (
    <div
      role="group"
      aria-label="Filter by department"
      className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0 sm:pb-0"
    >
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-pressed={value === null}
        className={chipClass(value === null)}
      >
        All departments
      </button>
      {options.map((d) => {
        const selected = value === d.slug;
        return (
          <button
            key={d.slug}
            type="button"
            onClick={() => onChange(selected ? null : d.slug)}
            aria-pressed={selected}
            className={chipClass(selected)}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks pulse — Humus hero with roll-up progress across every active
// project in view. White/Linen + Bright Moss on Humus is an approved
// brand colourway, and the hexagon is the brand's graphic device.
// ---------------------------------------------------------------------------

function TasksPulse({
  active,
  department,
}: {
  active: ProjectSummary[];
  department: DepartmentSlug | null;
}) {
  const { data: myTasks } = useMyOpenTasks();

  const totals = useMemo(() => {
    let tasks = 0;
    let open = 0;
    let overdue = 0;
    for (const p of active) {
      tasks += p.taskCount;
      open += p.openTaskCount;
      overdue += p.overdueTaskCount;
    }
    return { tasks, open, done: tasks - open, overdue };
  }, [active]);

  const mine = useMemo(() => {
    const rows = (myTasks ?? []).filter(
      (t) => !department || t.project.department === department,
    );
    const overdue = rows.filter((t) => dueState(t.due_date) === 'overdue');
    return { open: rows.length, overdue: overdue.length };
  }, [myTasks, department]);

  const pct = completionPercent(totals.tasks, totals.open);
  const scopeLabel = department
    ? (departmentMeta(department)?.label ?? 'department')
    : null;

  return (
    <section
      aria-label="Tasks pulse"
      className="relative overflow-hidden rounded-lg bg-humus p-5 text-linen sm:p-6"
    >
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 text-bright-moss/15 sm:-right-10"
      >
        <path
          d={HEXAGON_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
      </svg>
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        className="pointer-events-none absolute -bottom-10 right-24 hidden h-28 w-28 text-atmosphere/15 sm:block"
      >
        <path
          d={HEXAGON_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      </svg>

      <div className="relative space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wider text-bright-moss">
              {scopeLabel ? `${scopeLabel} pulse` : 'Tasks pulse'}
            </p>
            <p className="flex items-baseline gap-2">
              <span className="text-4xl font-light tracking-headline tabular-nums">
                {pct}%
              </span>
              <span className="text-sm text-linen/75">complete</span>
            </p>
          </div>
          <p className="text-xs text-linen/70">
            {totals.done} of {totals.tasks}{' '}
            {totals.tasks === 1 ? 'task' : 'tasks'} done across{' '}
            {active.length} active{' '}
            {active.length === 1 ? 'project' : 'projects'}
          </p>
        </div>

        <CompletionBar
          value={pct}
          tone="onDark"
          label="Completion across active projects"
          className="h-2"
        />

        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <PulseStat
            label="Active projects"
            value={active.length}
            dot="bg-bright-moss"
          />
          <PulseStat label="Open tasks" value={totals.open} dot="bg-atmosphere" />
          <PulseStat
            label="Overdue tasks"
            value={totals.overdue}
            dot="bg-fresh-salmon"
            highlight={totals.overdue > 0}
          />
          <PulseStat
            label="Your overdue"
            value={mine.overdue}
            dot="bg-fresh-salmon"
            highlight={mine.overdue > 0}
          />
        </dl>
      </div>
    </section>
  );
}

function PulseStat({
  label,
  value,
  dot,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  dot: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5',
        highlight
          ? 'border-fresh-salmon/40 bg-fresh-salmon/10'
          : 'border-linen/10 bg-linen/[0.06]',
      )}
    >
      <dt className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-linen/70">
        <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dot)} />
        <span className="truncate">{label}</span>
      </dt>
      <dd className="mt-1 text-xl font-medium tabular-nums tracking-headline text-linen">
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Your tasks" — every open task assigned to the signed-in user across
// all the projects they can see, grouped by urgency: overdue, due
// today, upcoming, then the undated backlog. Each row labels its
// project (and department) so the operator can fan out without losing
// context. Overdue and today are always shown in full; the two long
// tails start folded so the page stays scannable.
// ---------------------------------------------------------------------------

const GROUP_ORDER: DueState[] = ['overdue', 'today', 'upcoming', 'none'];

const GROUP_META: Record<
  DueState,
  { label: string; initialLimit: number; tone: string }
> = {
  overdue: { label: 'Overdue', initialLimit: Infinity, tone: 'text-fresh-salmon-dark' },
  today: { label: 'Due today', initialLimit: Infinity, tone: 'text-info' },
  upcoming: { label: 'Upcoming', initialLimit: 10, tone: 'text-muted-foreground' },
  none: { label: 'No due date', initialLimit: 5, tone: 'text-muted-foreground' },
};

function MyTasksCard({ department }: { department: DepartmentSlug | null }) {
  const updateTask = useUpdateTask();
  const { data: tasks, isLoading } = useMyOpenTasks();
  const { data: load } = useMyWorkloadThisWeek();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<DueState>>(() => new Set());

  // Local optimistic-complete set. The query filters on
  // `is_completed = false` server-side, so a freshly-completed task
  // will drop out of the list on the next refetch. We hide it
  // immediately on click so the operator gets the instant "checked
  // off" feedback the project detail page has, then clear the entry
  // once the refetched list no longer contains it. Keeping the row
  // around (instead of full removal) until the refetch lands also
  // means we never flicker an empty state during the round-trip.
  const [pendingComplete, setPendingComplete] = useState<Set<string>>(
    () => new Set(),
  );

  // Once the refetched data drops a pending id, we no longer need to
  // remember it locally — keeps the set from growing unbounded across
  // a long session and avoids stale ids resurfacing if a server-side
  // change re-opens a task. We only mutate state when a difference
  // exists so this effect doesn't loop.
  useEffect(() => {
    if (pendingComplete.size === 0 || !tasks) return;
    const stillVisible = new Set(tasks.map((t) => t.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of pendingComplete) {
      if (stillVisible.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setPendingComplete(next);
  }, [tasks, pendingComplete]);

  const onComplete = (task: MyTaskRow) => {
    // Optimistically hide the row before the request settles.
    setPendingComplete((prev) => {
      if (prev.has(task.id)) return prev;
      const next = new Set(prev);
      next.add(task.id);
      return next;
    });
    updateTask.mutate(
      {
        id: task.id,
        projectId: task.project.id,
        patch: { is_completed: true },
      },
      {
        onSuccess: () => {
          // The shared `useUpdateTask` mutation already invalidates
          // `projectKeys.tasks(projectId)` + `projectKeys.lists()` for
          // the project-scoped views, but this page reads from a
          // separate `taskKeys.all + 'mine'` cache. Invalidate that
          // here so the row falls off the list once the DB confirms
          // the completion.
          void qc.invalidateQueries({
            queryKey: [...taskKeys.all, 'mine'],
          });
        },
        onError: (err) => {
          setPendingComplete((prev) => {
            if (!prev.has(task.id)) return prev;
            const next = new Set(prev);
            next.delete(task.id);
            return next;
          });
          const msg =
            err instanceof Error ? err.message : 'Could not complete task';
          toast.error(msg);
        },
      },
    );
  };

  const groups = useMemo(() => {
    const visible = (tasks ?? []).filter(
      (t) =>
        !pendingComplete.has(t.id) &&
        (!department || t.project.department === department),
    );
    const byState = new Map<DueState, MyTaskRow[]>();
    for (const state of GROUP_ORDER) byState.set(state, []);
    for (const t of visible) byState.get(dueState(t.due_date))!.push(t);
    return {
      total: visible.length,
      list: GROUP_ORDER.map((state) => ({
        state,
        meta: GROUP_META[state],
        tasks: byState.get(state)!,
      })).filter((g) => g.tasks.length > 0),
    };
  }, [tasks, pendingComplete, department]);

  const toggleExpanded = (state: DueState) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });

  const rowsFor = (g: (typeof groups.list)[number]) =>
    expanded.has(g.state) ? g.tasks : g.tasks.slice(0, g.meta.initialLimit);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 p-4 sm:p-6 sm:pb-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-info-soft/40 text-info">
            <CalendarClock className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <CardTitle>Your tasks</CardTitle>
          {groups.total > 0 && (
            <Badge variant="info-soft" className="px-2 py-0 tabular-nums">
              {groups.total}
            </Badge>
          )}
        </div>
        <WorkloadSummary load={load} />
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded" />
            ))}
          </div>
        ) : groups.total === 0 ? (
          <div className="px-4 pb-6 sm:px-6">
            <EmptyState
              icon={ListTodo}
              title={
                department
                  ? 'Nothing assigned to you in this department'
                  : "You're all caught up"
              }
            />
          </div>
        ) : isMobile ? (
          // Each task is a card with a 44px completion target on the
          // left (marks complete, doesn't navigate) and a stretched
          // title link that opens the task in its project.
          <div className="space-y-4 px-3 pb-3">
            {groups.list.map((g) => {
              const rows = rowsFor(g);
              const hidden = g.tasks.length - rows.length;
              return (
                <div key={g.state} className="space-y-2">
                  <GroupHeading
                    label={g.meta.label}
                    count={g.tasks.length}
                    tone={g.meta.tone}
                    className="px-1"
                  />
                  <MobileListCardStack>
                    {rows.map((t) => (
                      <MyTaskMobileCard
                        key={t.id}
                        task={t}
                        onComplete={onComplete}
                      />
                    ))}
                  </MobileListCardStack>
                  <ShowMoreButton
                    hidden={hidden}
                    expanded={expanded.has(g.state)}
                    onToggle={() => toggleExpanded(g.state)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pl-6 pr-0">
                  <span className="sr-only">Complete</span>
                </TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="hidden sm:table-cell">Workload</TableHead>
                <TableHead className="hidden sm:table-cell">Priority</TableHead>
                <TableHead className="hidden sm:table-cell pr-6">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.list.map((g) => {
                const rows = rowsFor(g);
                const hidden = g.tasks.length - rows.length;
                return (
                  <Fragment key={g.state}>
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={7} className="bg-muted/30 py-1.5 pl-6">
                        <GroupHeading
                          label={g.meta.label}
                          count={g.tasks.length}
                          tone={g.meta.tone}
                        />
                      </TableCell>
                    </TableRow>
                    {rows.map((t) => (
                      <MyTaskRowView key={t.id} task={t} onComplete={onComplete} />
                    ))}
                    {hidden > 0 || expanded.has(g.state) ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={7} className="py-1 pl-6">
                          <ShowMoreButton
                            hidden={hidden}
                            expanded={expanded.has(g.state)}
                            onToggle={() => toggleExpanded(g.state)}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function GroupHeading({
  label,
  count,
  tone,
  className,
}: {
  label: string;
  count: number;
  tone: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider',
        tone,
        className,
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums opacity-70">{count}</span>
    </div>
  );
}

function ShowMoreButton({
  hidden,
  expanded,
  onToggle,
}: {
  hidden: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (hidden <= 0 && !expanded) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-9 px-2 text-xs text-muted-foreground"
      onClick={onToggle}
    >
      <ChevronDown
        className={cn(
          'h-3.5 w-3.5 transition-transform duration-200',
          expanded && 'rotate-180',
        )}
      />
      {expanded ? 'Show fewer' : `Show ${hidden} more`}
    </Button>
  );
}

function MyTaskMobileCard({
  task,
  onComplete,
}: {
  task: MyTaskRow;
  onComplete: (task: MyTaskRow) => void;
}) {
  const priority = PRIORITY_META[task.priority];
  const status = STATUS_META[task.status];
  const workload = formatWorkloadHours(task.approximate_workload_hours);
  const department = departmentMeta(task.project.department);
  const subtitleParts = [
    task.project.name,
    department?.label ?? null,
    workload || null,
  ].filter(Boolean);
  return (
    <MobileListCard
      className={cn(
        dueState(task.due_date) === 'overdue' &&
          'border-l-[3px] border-l-fresh-salmon',
      )}
      leading={
        <button
          type="button"
          onClick={() => onComplete(task)}
          aria-label={`Mark “${task.name}” as completed`}
          className="relative z-10 -m-3 flex h-11 w-11 items-center justify-center rounded-full"
        >
          <span className="h-5 w-5 rounded-full border-[1.5px] border-old-growth/50 bg-card transition-colors hover:border-bright-moss-dark" />
        </button>
      }
      title={
        <Link
          to={`/projects/${task.project.id}?task=${task.id}`}
          className="after:absolute after:inset-0 after:rounded-lg after:content-['']"
        >
          {task.name}
        </Link>
      }
      subtitle={subtitleParts.join(' · ')}
      badges={
        <>
          <DueChip due={task.due_date} />
          <Badge className={cn('font-normal', priority.className)}>
            {priority.label}
          </Badge>
          <Badge className={cn('font-normal', status.className)}>
            {status.label}
          </Badge>
        </>
      }
      trailing={null}
    />
  );
}

function MyTaskRowView({
  task,
  onComplete,
}: {
  task: MyTaskRow;
  onComplete: (task: MyTaskRow) => void;
}) {
  const due = dueState(task.due_date);
  const priority = PRIORITY_META[task.priority];
  const status = STATUS_META[task.status];
  const department = departmentMeta(task.project.department);

  return (
    <TableRow>
      <TableCell className="w-10 pl-6 pr-0">
        <Checkbox
          checked={false}
          ariaLabel={`Mark “${task.name}” as completed`}
          onCheckedChange={() => onComplete(task)}
        />
      </TableCell>
      <TableCell>
        <Link
          to={`/projects/${task.project.id}?task=${task.id}`}
          className="font-medium text-foreground hover:underline"
        >
          {task.name}
        </Link>
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-0.5">
          <Link
            to={`/projects/${task.project.id}`}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            {task.project.name}
          </Link>
          {department && (
            <span className="text-[11px] text-muted-foreground/80">
              {department.label}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {task.due_date ? (
          <div className="flex flex-col items-start gap-0.5">
            <DueChip due={task.due_date} className="-ml-2" />
            {due !== 'today' && (
              <span
                className={cn(
                  'text-xs',
                  due === 'overdue'
                    ? 'text-fresh-salmon-dark'
                    : 'text-muted-foreground',
                )}
              >
                {formatRelative(task.due_date)}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        {formatWorkloadHours(task.approximate_workload_hours) || (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <Badge className={cn('whitespace-nowrap font-normal', priority.className)}>
          {priority.label}
        </Badge>
      </TableCell>
      <TableCell className="hidden sm:table-cell pr-6">
        <Badge className={cn('whitespace-nowrap font-normal', status.className)}>
          {status.label}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

function WorkloadSummary({
  load,
}: {
  load: { todayHours: number; weekHours: number } | undefined;
}) {
  if (!load || (load.todayHours <= 0 && load.weekHours <= 0)) {
    return (
      <span className="hidden text-xs text-muted-foreground sm:inline">
        Across every project you're on
      </span>
    );
  }
  const todayHeavy = isHeavyDay(load.todayHours);
  const weekHeavy = isHeavyWeek(load.weekHours);
  return (
    <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
      <span
        className={cn(
          weekHeavy ? 'font-medium text-fresh-salmon-dark' : 'text-muted-foreground',
        )}
        title={
          weekHeavy
            ? `Over a typical ${WORKWEEK_HOURS}h week — worth a conversation about what’s realistic.`
            : `Estimated open work due this week (Mon–Sun). A typical week is ${WORKWEEK_HOURS}h.`
        }
      >
        This week {formatWorkloadHours(load.weekHours)}
      </span>
      {load.todayHours > 0 && (
        <span
          className={cn(
            todayHeavy
              ? 'font-medium text-fresh-salmon-dark'
              : 'text-muted-foreground',
          )}
          title={
            todayHeavy
              ? `Over a typical ${WORKDAY_HOURS}h day.`
              : `Estimated open work due today. A typical day is ${WORKDAY_HOURS}h.`
          }
        >
          Today {formatWorkloadHours(load.todayHours)}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project grid
// ---------------------------------------------------------------------------

function ProjectGrid({
  projects,
  droppableId,
  draggable,
}: {
  projects: ProjectSummary[];
  droppableId: string;
  draggable: boolean;
}) {
  if (!draggable) {
    return (
      <div className={PROJECT_GRID}>
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>
    );
  }
  return (
    <Droppable droppableId={droppableId}>
      {(dropProvided) => (
        <div
          ref={dropProvided.innerRef}
          {...dropProvided.droppableProps}
          className={PROJECT_GRID}
        >
          {projects.map((p, idx) => (
            <DraggableProjectCard key={p.id} project={p} index={idx} />
          ))}
          {dropProvided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

/**
 * Wraps a single `<ProjectCard>` in a `<Draggable>`. The drag handle
 * is a grip icon in the card header so the rest of the card (stretched
 * link to the project, dropdown, etc.) keeps its normal click
 * behaviour — without a dedicated handle the whole card surface
 * intercepts pointer events and the project link stops working.
 */
function DraggableProjectCard({
  project,
  index,
}: {
  project: ProjectSummary;
  index: number;
}) {
  return (
    <Draggable draggableId={project.id} index={index}>
      {(dragProvided, snapshot) => (
        <div
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          className={cn(
            'h-full rounded-lg transition-shadow',
            snapshot.isDragging && 'shadow-xl ring-2 ring-old-growth/40',
          )}
        >
          <ProjectCard
            project={project}
            dragHandleProps={dragProvided.dragHandleProps}
          />
        </div>
      )}
    </Draggable>
  );
}

// ---------------------------------------------------------------------------
// Project card — also the inline home for close / reopen / delete
// so the operator can clean up from the Tasks page without having to
// drill into each project first.
// ---------------------------------------------------------------------------

type ProjectHealth = 'archived' | 'empty' | 'complete' | 'behind' | 'active';

const HEALTH_BADGE: Record<
  ProjectHealth,
  {
    label: string;
    variant: 'muted' | 'success-soft' | 'warning-soft' | 'info-soft' | 'outline';
  }
> = {
  archived: { label: 'Archived', variant: 'muted' },
  empty: { label: 'No tasks yet', variant: 'outline' },
  complete: { label: 'Complete', variant: 'success-soft' },
  behind: { label: 'Overdue tasks', variant: 'warning-soft' },
  active: { label: 'In progress', variant: 'info-soft' },
};

function projectHealth(p: ProjectSummary): ProjectHealth {
  if (p.archived_at) return 'archived';
  if (p.taskCount === 0) return 'empty';
  if (p.openTaskCount === 0) return 'complete';
  if (p.overdueTaskCount > 0) return 'behind';
  return 'active';
}

function ProjectCard({
  project,
  dragHandleProps,
}: {
  project: ProjectSummary;
  // `@hello-pangea/dnd` types this as `DraggableProvidedDragHandleProps |
  // null | undefined`. We thread it through so the card renders the
  // grip in the same DOM cell as the rest of the header.
  dragHandleProps?: React.HTMLAttributes<HTMLElement> | null;
}) {
  const { profile } = useAuth();
  const archive = useArchiveProject();
  const isMobile = useIsMobile();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const isArchived = !!project.archived_at;
  // Any member of the project (i.e. anyone for whom the row showed up)
  // can close / reopen via the set_project_archived RPC. Delete stays
  // gated to the creator + super_admin to match the RLS delete policy.
  const canArchive = !!profile;
  const canDelete =
    !!profile &&
    (profile.role === 'super_admin' || project.created_by === profile.id);
  const showMenu = canArchive || canDelete;

  const members = project.project_members
    .map((m) => m.profiles)
    .filter((p): p is NonNullable<typeof p> => !!p);
  const pct = completionPercent(project.taskCount, project.openTaskCount);
  const health = projectHealth(project);
  const healthBadge = HEALTH_BADGE[health];
  const department = departmentMeta(project.department);

  const onToggleArchive = async () => {
    try {
      await archive.mutateAsync({ id: project.id, archived: !isArchived });
      toast.success(
        isArchived
          ? `Reopened “${project.name}”`
          : `Closed “${project.name}”`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed';
      toast.error(msg);
    }
  };

  const menuTriggerClass =
    'relative z-10 -mr-2 -mt-1 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground sm:h-9 sm:w-9';

  return (
    <>
      <Card
        className={cn(
          'group relative flex h-full flex-col transition-colors hover:border-old-growth/40 focus-within:border-old-growth/40',
          isArchived && 'bg-muted/30',
        )}
      >
        <CardContent className="flex h-full flex-col gap-4 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            {dragHandleProps && (
              <button
                type="button"
                {...dragHandleProps}
                aria-label={`Reorder ${project.name}`}
                className="relative z-10 -ml-2 mt-2 cursor-grab rounded p-0.5 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 active:cursor-grabbing [@media(hover:none)]:opacity-100"
              >
                <GripVertical className="h-4 w-4" strokeWidth={1.6} />
              </button>
            )}
            <ProjectMonogram
              id={project.id}
              name={project.name}
              muted={isArchived}
            />
            <div className="min-w-0 flex-1 space-y-0.5">
              <Link
                to={`/projects/${project.id}`}
                className={cn(
                  'block truncate text-base font-medium tracking-headline leading-headline text-foreground',
                  "after:absolute after:inset-0 after:rounded-lg after:content-['']",
                  'group-hover:underline',
                  isArchived && 'text-muted-foreground',
                )}
              >
                {project.name}
              </Link>
              <p className="truncate text-xs text-muted-foreground">
                {department
                  ? `${department.label} · Created ${formatRelative(project.created_at)}`
                  : `Created ${formatRelative(project.created_at)}`}
              </p>
            </div>
            {showMenu &&
              (isMobile ? (
                <button
                  type="button"
                  className={menuTriggerClass}
                  aria-label={`Actions for ${project.name}`}
                  onClick={() => setActionsOpen(true)}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={menuTriggerClass}
                      aria-label={`Actions for ${project.name}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Project</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {canArchive && (
                      <DropdownMenuItem
                        onSelect={onToggleArchive}
                        disabled={archive.isPending}
                      >
                        {isArchived ? (
                          <>
                            <ArchiveRestore className="mr-2 h-4 w-4" />
                            Reopen project
                          </>
                        ) : (
                          <>
                            <Archive className="mr-2 h-4 w-4" />
                            Close project
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <>
                        {canArchive && <DropdownMenuSeparator />}
                        <DropdownMenuItem
                          onSelect={() => setDeleteOpen(true)}
                          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete project…
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Badge variant={healthBadge.variant} className="font-normal">
                {healthBadge.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                <span className="text-sm font-medium tabular-nums text-foreground">
                  {pct}%
                </span>{' '}
                complete
              </span>
            </div>
            <CompletionBar
              value={pct}
              tone={health === 'behind' ? 'warning' : 'default'}
              label={`${project.name} completion`}
            />
          </div>

          <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3 text-xs">
            {members.length > 0 ? (
              <AvatarStack people={members} />
            ) : (
              <span className="text-muted-foreground">No members yet</span>
            )}
            <span className="text-muted-foreground">
              <span className="font-medium tabular-nums text-foreground">
                {project.openTaskCount}
              </span>{' '}
              open · {project.taskCount}{' '}
              {project.taskCount === 1 ? 'task' : 'tasks'}
            </span>
          </div>
        </CardContent>
      </Card>

      {isMobile && showMenu && (
        <ActionSheet
          open={actionsOpen}
          onOpenChange={setActionsOpen}
          title={project.name}
          description={
            department
              ? `${department.label} · Created ${formatRelative(project.created_at)}`
              : `Created ${formatRelative(project.created_at)}`
          }
        >
          {canArchive && (
            <ActionSheetItem
              icon={isArchived ? ArchiveRestore : Archive}
              label={isArchived ? 'Reopen project' : 'Close project'}
              disabled={archive.isPending}
              onSelect={() => {
                setActionsOpen(false);
                void onToggleArchive();
              }}
            />
          )}
          {canDelete && (
            <ActionSheetItem
              icon={Trash2}
              label="Delete project…"
              destructive
              onSelect={() => {
                setActionsOpen(false);
                setDeleteOpen(true);
              }}
            />
          )}
        </ActionSheet>
      )}

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectId={project.id}
        projectName={project.name}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation — same type-the-name gate as the detail page,
// duplicated here so a card-level delete doesn't need a navigation
// round-trip first.
// ---------------------------------------------------------------------------

function DeleteProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
}) {
  const deleteProject = useDeleteProject();
  const [confirmText, setConfirmText] = useState('');

  // Reset on open so a previously typed value can't survive a Cancel
  // + reopen.
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const matches = confirmText.trim() === projectName;

  const onConfirm = async () => {
    if (!matches) return;
    try {
      await deleteProject.mutateAsync({ id: projectId });
      toast.success(`Deleted “${projectName}”`);
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delete project?</DialogTitle>
          <DialogDescription>
            This permanently removes{' '}
            <span className="font-medium text-foreground">“{projectName}”</span>{' '}
            and every task on it. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label
            htmlFor={`delete-project-confirm-${projectId}`}
            className="text-xs text-muted-foreground"
          >
            Type <span className="font-mono text-foreground">{projectName}</span>{' '}
            to confirm.
          </label>
          <Input
            id={`delete-project-confirm-${projectId}`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={projectName}
            autoComplete="off"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteProject.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!matches || deleteProject.isPending}
          >
            {deleteProject.isPending ? 'Deleting…' : 'Delete project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
